import { Action, AuthReducer, AuthState, initialState } from "./reducer"
import React, { Dispatch, ReactNode, useEffect, useMemo, useReducer, useRef } from "react"
import { useQueryCustomer, useQueryCustomerId } from "../../hooks/customer/use-query-customer"

import { hash } from "../../utils/hash"
import { logout } from "./actions"
import { setCookie } from "../../utils/cookie"
import { useAPIState } from "../client"
import { usePosthogLoaded } from "../../hooks/use-posthog-loaded"
import { useQueryClient } from "@tanstack/react-query"

const AuthStateContext = React.createContext<AuthState | undefined>(undefined)
const AuthDispatchContext = React.createContext<Dispatch<Action> | undefined>(undefined)

const useAuthState = () => {
    const context = React.useContext(AuthStateContext)
    if (context === undefined) {
        throw new Error("useAuthState must be used within a AuthProvider")
    }

    return context
}

const useAuthDispatch = () => {
    const context = React.useContext(AuthDispatchContext)
    if (context === undefined) {
        throw new Error("useAuthDispatch must be used within a AuthProvider")
    }

    return context
}

type AuthProviderProps = {
    children: ReactNode
}

const AuthProvider = ({ children }: AuthProviderProps) => {
    const [state, dispatch] = useReducer(AuthReducer, initialState)
    const consumerToken = useAPIState(s => s.consumerToken)
    const consumerTokenStatus = useAPIState(s => s.consumerTokenStatus)
    const customerWeakToken = useAPIState(s => s.customerWeakToken)

    const queryClient = useQueryClient()
    const hadConsumerTokenError = useRef(false)
    const hadCustomerWeakToken = useRef(false)

    // refetch everything that failed while the api was down, otherwise the page keeps
    // rendering its error state even though the consumer auth recovered in the background
    useEffect(() => {
        if (consumerTokenStatus === "error") {
            hadConsumerTokenError.current = true
            return
        }

        if (consumerTokenStatus !== "success" || !hadConsumerTokenError.current) return

        hadConsumerTokenError.current = false
        queryClient.invalidateQueries()
    }, [consumerTokenStatus, queryClient])

    // clean up after a session, no matter whether the user logged out or the token expired
    useEffect(() => {
        if (customerWeakToken) {
            hadCustomerWeakToken.current = true
            return
        }

        if (!hadCustomerWeakToken.current) return

        hadCustomerWeakToken.current = false
        logout(dispatch)
    }, [customerWeakToken])

    const { error: customerIdError } = useQueryCustomerId({ background: true })

    const { data: customerData, error: customerError } = useQueryCustomer({ background: true })

    const reference = customerData?.reference
    const email = customerData?.address.email
    const isPartner = !!customerData?.features?.length

    const posthogLoaded = usePosthogLoaded()

    useEffect(() => {
        if (!reference) return

        window.exponea?.identify({ client_id: reference })
    }, [reference])

    useEffect(() => {
        if (!posthogLoaded || !reference || !email) return

        window.posthog?.identify(reference, { email })
    }, [posthogLoaded, reference, email])

    useEffect(() => {
        if (!email) return

        const pushHashedEmail = async () => {
            const sha256_email = await hash(email.toLowerCase())

            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: "email",
                sha256_email: sha256_email,
            })
        }

        pushHashedEmail()
    }, [email])

    useEffect(() => {
        if (!reference) return

        setCookie("__is_partner__", isPartner, { sameSite: "Lax" })
    }, [reference, isPartner])

    const isCustomer = !!customerWeakToken
    const isCustomerLoading = isCustomer && !customerData && !customerError && !customerIdError
    const isInitialLoading = !consumerToken && consumerTokenStatus !== "error"

    const value = useMemo(
        () => ({
            ...state,
            isInitialLoading,
            isCustomer,
            isCustomerLoading,
            isPartner,
        }),
        [state, isInitialLoading, isCustomer, isPartner, isCustomerLoading]
    )

    return (
        <AuthStateContext.Provider value={value}>
            <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
        </AuthStateContext.Provider>
    )
}

export { useAuthState, useAuthDispatch, AuthProvider }
